import React, { useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import { useQuery } from '@apollo/client';
import { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import dynamic from 'next/dynamic';
import { Footer } from '@/components/Footer/Footer';
import { PageTopControls } from '@/components/PageTopControls';
import { Breadcrumbs } from '@/components/Breadcrumbs/Breadcrumbs';
import { Button } from '@/components/UI/Button/Button';
import { HeartIcon, ShareIcon } from '@/components/UI/Icons/Icons';
import * as S from '@/views/Ads/Ad.styles';
import { MainInfo } from '@/components/Ad/MainInfo/MainInfo';
import { Status } from '@/components/Ad/Status/Status';
import { Settings } from '@/components/Ad/Settings/Settings';
import { Properties } from '@/components/Ad/Properties/Properties';
import { BankPromo } from '@/components/Ad/BankPromo/BankPromo';
import { Description } from '@/components/Ad/Description/Description';
import { AuthorInfo } from '@/components/Ad/AuthorInfo/AuthorInfo';
import { Promotions } from '@/components/Ad/Promotions/Promotions';
import { Complaint } from '@/components/Ad/Complaint';
import { Overview } from '@/components/Ad/Overview/Overview';
import { SliderWithGallery } from '@/components/Ad/SliderWithGallery/SliderWithGallery';
import { AdStatus } from '@/types';
import { ErrorView } from '@/views/Error/Error.view';
import { FloatingButtons } from '@/components/Ad/FloatingButtons/FloatingButtons';
import { useShareData } from '@/hooks/useShareData';
import { MAX_RELATED_ADS } from '@/views/Ads/Ad.view.constants';
import { useToastStore } from '@/store/useToastStore';
import { useAdDetails } from '@/hooks/useAdDetails';
import { WarningPendingState } from '@/components/Ad/WarningPendingState/WarningPendingState';
import { useIsAuthor } from '@/hooks/useIsAuthor';
import { useIsShop } from '@/hooks/useIsShop';
import { useBanner } from '@/hooks/useBanner';
import { GetUserResponse } from '@/api/types/user.types';
import { GET_USER } from '@/api/queries/user.queries';
import { Toast } from '@/components/UI/Toast';
import { WebViewPaymentStatusModal } from '@/components/Payment/WebViewPaymentStatusModal/WebViewPaymentStatusModal';
import { PaymentInTerminalsAfterAuth } from '@/components/Payment/PaymentInTerminalsAfterAuth';
import { SkeletonLoader } from '@/components/SkeletonLoader/SkeletonLoader';
import { useToggleOpenState } from '@/hooks/useToggleOpenState';
import { AdKind } from '@/api/types/common.types';
import { extractTargetParams } from '@/utils/helpers';
import { useServerDataContext } from '@/contexts';
import { useBookmark } from '@/hooks/useBookmark';
import { SuccessPaymentStatusModal } from '@/components/Payment/SuccessPaymentStatusModal';
import { Header } from '@/components/Header';
import { getCurrentCategoryPath } from '@/hooks/useCurrentCategory/useCurrentCategory.helpers';
import { buildBreadcrumbs } from '@/components/Breadcrumbs/Breadcrumbs.helpers';
import { CollectionMasterSheet } from '@/features/BookmarkCollections/components';
import { AdPageTestIds } from '@/__e2e__/testIds/ad-page';
import NoSsr from '@/components/NoSsr/NoSsr';
import { RelatedAds } from '@/components/Ad/RelatedAds/RelatedAds';
import { TransitionSourceBlockEnum } from '@/api/types/ads.types';
import { useResponsive } from '@/hooks/useResponsive';
import { GetCategoryBreadcrumbsResponse } from '@/api/types/categories.types';
import { GET_CATEGORY_BREADCRUMBS_DATA } from '@/api/queries/categories.queries';
import { Statistics } from '@/components/Ad/Statistics/Statistics';
import { PriceAndContacts } from '@/components/Ad/PriceAndContacts/PriceAndContacts';

const CategoryPathSynchronizer = dynamic(
  () =>
    import('@/components/CategoryPathSynchronizer').then(
      mod => mod.CategoryPathSynchronizer
    ),
  { ssr: false }
);

const PromotionFlow = dynamic(
  () =>
    import('@/components/Ad/Promotions/PromotionFlow').then(
      mod => mod.PromotionFlow
    ),
  { ssr: false }
);

export const AdView = () => {
  const isBrowser = typeof window !== 'undefined';
  const { t } = useTranslation();
  const theme = useTheme();
  const router = useRouter();
  const { adDetails, loading } = useAdDetails();
  const { initialAdDetails, initialCategoryBreadcrumbs } =
    useServerDataContext();
  const adData = adDetails ?? initialAdDetails;
  const shop = adData?.shop;
  const [isGalleryOpen, toggleGallery] = useToggleOpenState();
  const [isPromotionFlowOpen, togglePromotionFlow] = useToggleOpenState();
  const [selectedPromotionType, setSelectedPromotionType] =
    useState<AdKind | null>(null);
  const { isDesktop } = useResponsive();

  const targetParams = useMemo(
    () => extractTargetParams(adData ?? undefined),
    [adData]
  );

  const { topBanner } = useBanner({
    targetParams: targetParams || {},
    canLoadTopBanner: !loading && !!targetParams,
  });

  const isAuthor = useIsAuthor();
  const isShop = useIsShop(adData);

  const { data: userInfo } = useQuery<GetUserResponse>(GET_USER);
  const { data: clientCategoryBreadcrumbs } =
    useQuery<GetCategoryBreadcrumbsResponse>(GET_CATEGORY_BREADCRUMBS_DATA, {
      variables: { path: getCurrentCategoryPath(router.query.slug) },
      skip: !isBrowser,
    });

  const { openToast, closeToast } = useToastStore();

  const categoryBreadcrumbs =
    initialCategoryBreadcrumbs ?? clientCategoryBreadcrumbs?.category;
  const currentCategoryPath = categoryBreadcrumbs?.path;

  const status = adData?.status;
  const { shareUrl, canShare } = useShareData();
  const { isBookmarked: isFavorite, toggleBookmark } = useBookmark({
    ad: adData ?? undefined,
  });

  const [isCollectionMasterOpen, setIsCollectionMasterOpen] = useState(false);

  useEffect(() => {
    closeToast();
  }, [isGalleryOpen]);

  function handleBookmarkClick() {
    toggleBookmark();

    if (!isFavorite) {
      handleAddToCollection();
    } else {
      openToast({
        isLight: isGalleryOpen,
        text: t('bookmark_collections.unbookmarked'),
      });
    }
  }

  function handleAddToCollection() {
    openToast({
      isLight: isGalleryOpen,
      text: t('bookmark_collections.toast_tip'),
      action: t('bookmark_collections.toast_action'),
      onActionClick: handleToastActionClick,
    });
  }

  function handleToastActionClick() {
    closeToast();
    toggleGallery(false);
    setIsCollectionMasterOpen(true);
  }

  function handleSelectPromotion(promotionType: AdKind) {
    setSelectedPromotionType(promotionType);
    togglePromotionFlow();
  }

  const { Published, Expired, Archived } = AdStatus;
  const isExpiredOrPublished = status && [Published, Expired].includes(status);
  const canComplain =
    !isAuthor && isExpiredOrPublished && !userInfo?.currentUser?.isBanned;
  const canBookmark =
    status === Published ||
    (!!status && [Expired, Archived].includes(status) && isAuthor);
  const shouldShowNotFound = !adData || (!isAuthor && !isExpiredOrPublished);
  const isShareVisible = canShare && status === Published;
  const isRelatedAdsVisible =
    (adData?.relatedAds?.length ?? 0) > 0 || (loading && isBrowser);

  // TODO: set_edit_limit_reached param needed only for rails ad page. Remove this after implementing responsive ad page
  useEffect(() => {
    if (router.query.set_edit_limit_reached) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars,camelcase
      const { set_edit_limit_reached, ...cleanQuery } = router.query;
      const currentHash = window.location.hash;
      router.replace({ query: cleanQuery, hash: currentHash }, undefined, {
        shallow: true,
      });
    }
  }, [router]);

  function getHeaderActions() {
    if (loading) {
      return (
        <S.AdHeaderActions>
          <SkeletonLoader width="17px" height="20px" />
          <SkeletonLoader width="20px" height="20px" />
        </S.AdHeaderActions>
      );
    }

    return (
      <S.AdHeaderActions>
        {isShareVisible && (
          <Button
            appearance="transparent"
            hasPadding={false}
            onClick={shareUrl}
            aria-label="Share"
          >
            <ShareIcon
              stroke={theme.palette.primary.labels}
              className="share-btn js-share-btn"
            />
          </Button>
        )}

        {canBookmark && (
          <Button
            appearance="transparent"
            aria-label="Toggle bookmark"
            hasPadding={false}
            onClick={handleBookmarkClick}
            data-stat={
              !isFavorite && isShop ? 'shop-ad-bookmarking' : undefined
            }
            className={!isFavorite ? 'add_bookmark' : undefined}
          >
            <HeartIcon
              data-testid={AdPageTestIds.headerActions.bookmarkIcon}
              fill={isFavorite ? theme.palette.brand.main : 'none'}
              stroke={
                isFavorite
                  ? theme.palette.brand.main
                  : theme.palette.primary.labels
              }
            />
          </Button>
        )}
      </S.AdHeaderActions>
    );
  }

  if (!loading && shouldShowNotFound) {
    return (
      <ErrorView
        title={t('pages.ad.nothing_found')}
        subtitle={t('pages.ad.nothing_found_ad_description')}
        hasTopBanner
      />
    );
  }

  const hasShowAllLink =
    (isShop && status === Published) ||
    (adData?.relatedAds?.length ?? 0) > MAX_RELATED_ADS;

  const shouldShowBanner = !isShop && status !== AdStatus.Pending;
  const hasFloatingButtons =
    !isDesktop && !isAuthor && status === AdStatus.Published;
  const shouldShowBurgerInHeader = status === AdStatus.Pending;

  return (
    <>
      <CategoryPathSynchronizer />

      <NoSsr>{shouldShowBanner && topBanner}</NoSsr>

      <PageTopControls>
        <Header
          isServerRendered={false}
          hasBurger={shouldShowBurgerInHeader}
          rightAction={getHeaderActions()}
        />
      </PageTopControls>

      <S.Wrapper>
        <S.Container>
          <Breadcrumbs
            hasBg
            hasHorizontalPadding={!isDesktop}
            breadcrumbs={buildBreadcrumbs(categoryBreadcrumbs)}
            nestingLvl={
              getCurrentCategoryPath(router.query.slug).split('/').length
            }
          />
          {isDesktop && (
            <MainInfo
              canBookmark={canBookmark}
              isBookmarked={isFavorite}
              onBookmarkClick={handleBookmarkClick}
            />
          )}
          <S.Grid>
            <S.Section>
              {isAuthor && status === AdStatus.Pending && (
                <WarningPendingState />
              )}

              <SliderWithGallery
                isGalleryOpen={isGalleryOpen}
                toggleGallery={toggleGallery}
                canBookmark={canBookmark}
                isBookmarked={isFavorite}
                toggleBookmark={handleBookmarkClick}
                {...(status === AdStatus.Expired &&
                  !isAuthor && { overlayText: t('common.ad_inactive') })}
              />
              {isDesktop && (
                <>
                  <Properties />
                  <Description />
                  {!isAuthor && <Overview />}
                </>
              )}
            </S.Section>

            <S.Section>
              <S.StickyContainer>
                {status && <Status status={status} isAuthor={isAuthor} />}

                <S.AdViewMain>
                  {isAuthor ? (
                    <>
                      {!isDesktop && <MainInfo />}
                      <Statistics />
                      <Promotions onSelectPromotion={handleSelectPromotion} />
                      <Settings />
                      {!isDesktop && <Properties />}
                      {!isDesktop && <BankPromo />}
                      {!isDesktop && <Description />}
                      <AuthorInfo />
                      {isDesktop && <BankPromo />}

                      <PromotionFlow
                        isOpen={isPromotionFlowOpen}
                        toggle={togglePromotionFlow}
                        type={selectedPromotionType}
                      />
                    </>
                  ) : (
                    <>
                      {!isDesktop && <MainInfo />}
                      {isDesktop && <PriceAndContacts />}
                      {!isDesktop && <Properties />}
                      {!isDesktop && <BankPromo />}
                      {!isDesktop && <Description />}
                      <AuthorInfo />
                      {isDesktop && <BankPromo />}
                      {!isDesktop && <Overview />}
                      {canComplain && <Complaint />}
                    </>
                  )}
                </S.AdViewMain>
              </S.StickyContainer>
            </S.Section>
          </S.Grid>

          <CollectionMasterSheet
            isOpen={isCollectionMasterOpen}
            onClose={() => setIsCollectionMasterOpen(false)}
          />

          {isRelatedAdsVisible ? null : <Toast />}

          {isRelatedAdsVisible && (
            <RelatedAds
              ads={adData?.relatedAds}
              isLoading={isBrowser && loading}
              chunkSize={MAX_RELATED_ADS}
              title={t(
                `pages.ad.${
                  isShop && status === AdStatus.Published
                    ? 'shop_ads'
                    : 'similar_ads'
                }`
              )}
              {...(hasShowAllLink && {
                showAllLink:
                  isShop && status === AdStatus.Published
                    ? shop?.uri
                    : `/${currentCategoryPath}`,
              })}
              transitionSource={
                isShop
                  ? TransitionSourceBlockEnum.OtherUserAds
                  : TransitionSourceBlockEnum.SimilarAds
              }
            />
          )}

          {hasFloatingButtons && <FloatingButtons />}

          <Footer {...(hasFloatingButtons && { bottomGutter: '100px' })} />
        </S.Container>
      </S.Wrapper>

      <PaymentInTerminalsAfterAuth adId={adData?.id} />

      <WebViewPaymentStatusModal />

      <SuccessPaymentStatusModal />
    </>
  );
};
